import { getBaseLayer, getContextDeep as getDataContextRecursive, getRootNode } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import { RootState } from "@builder/store/store";
import { DynamicDataContext } from "@common/build/activeEntityType";
import {
    BaseLayer,
    Build,
    BuildNode,
    BuildNodeAttributes,
    Condition,
    PaymentButtonNode,
    PaymentDetails,
    PopupNode,
    TextNode
} from "@common/build/types";
import { createSelector } from "@reduxjs/toolkit";

//Builder Selectors
const builderState = (state: RootState): BuilderState => state.builder;

export const getBuild = createSelector(builderState, (state): Build => {
    return state.build;
});

export const getSelectedNode = createSelector(builderState, (state): BuildNode | null => {
    const baseLayer = getBaseLayer(state);
    if (state.selectedNode) {
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode) {
            return selectedNode;
        }
    }
    return null;
});

export const getAllLayerNodes = createSelector(
    builderState,
    (
        state
    ): {
        [id: string]: BuildNode;
    } => {
        const baseLayer = getBaseLayer(state);
        return baseLayer.components;
    }
);

export const getSelectedNodeId = createSelector(builderState, (state): string | null => {
    return state.selectedNode;
});

export const getHoveredNode = createSelector(builderState, (state): string | null => {
    return state.hoveredNode?.id || null;
});

export const getSelectedNodeAttr = createSelector(builderState, (state): BuildNodeAttributes | null => {
    if (state.selectedNode) {
        return getBaseLayer(state).components[state.selectedNode].attr || null;
    }
    return null;
});

export const getSelectedNodeText = createSelector(builderState, (state): string | null => {
    if (state.selectedNode) {
        return getBaseLayer(state).components[state.selectedNode].text || null;
    }
    return null;
});

export const getSelectedNodeStyle = createSelector(builderState, (state): { [key: string]: string } | null => {
    if (state.selectedNode) {
        return (getBaseLayer(state).components[state.selectedNode]?.attr?.style as { [key: string]: string }) || null;
    }
    return null;
});

export const getCurrentLayer = createSelector(builderState, (state): BaseLayer => {
    return getBaseLayer(state);
});

export const getCurrentRoot = createSelector(builderState, (state): string => {
    return getBaseLayer(state)?.root || "";
});

export const getPages = createSelector(builderState, (state): BaseLayer[] => {
    return state.build.pages;
});

export const getCFDS = createSelector(builderState, (state): BaseLayer[] => {
    return state.build.cfds;
});

export const getCurrentLayerIndex = createSelector(builderState, (state): number => {
    return state.currentLayer.index;
});

export const getFrameSize = createSelector(builderState, (state): { x: number; y: number | "fit-content" } => {
    return state.frameSize;
});

export const getPopups = createSelector(builderState, (state): BaseLayer[] => {
    return state.build.popups;
});

// this just lookup actions id in the master dictionary to get the name of the action to display.
export const getOnClickTarget = createSelector(builderState, (state): string | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const actions = baseLayer.components[state.selectedNode]?.actionsBuilder;
        const actionMap = state.build.actionBuilder;

        if (actions && actionMap && Object.keys(actionMap).length > 0) {
            const actionNames = actions.map(action => actionMap[action]?.name).filter(Boolean);
            return actionNames.join(", ");
        }
    }
    return null;
});

export const getSelectedNodeActionBuilder = createSelector(builderState, state => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer && state.selectedNode && state.build.actionBuilder) {
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode?.actionsBuilder?.[0]) {
            return state.build.actionBuilder[selectedNode.actionsBuilder[0]] || null;
        }
    }
    return null;
});

export const getCustomCSS = createSelector(builderState, (state): React.CSSProperties | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode) {
            if (selectedNode.customCSS) {
                return selectedNode.customCSS;
            }
        }
    }
    return null;
});

export const getBackdropToggle = createSelector(builderState, (state): boolean | null => {
    const baseLayer = getBaseLayer(state);
    const root = getRootNode(baseLayer) as PopupNode;
    if (root) {
        return root.backdropClose;
    }
    return null;
});

export const getFallbackText = createSelector(builderState, (state): { value: string; style?: React.CSSProperties } | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode] as TextNode;
        if (selectedNode) {
            if (selectedNode.fallback) {
                return selectedNode.fallback;
            }
        }
    }
    return null;
});

export const getPrepend = createSelector(builderState, (state): { value: string; style?: React.CSSProperties } | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode] as TextNode;
        if (selectedNode) {
            if (selectedNode.prepend) {
                return selectedNode.prepend;
            }
        }
    }
    return null;
});

export const getAppend = createSelector(builderState, (state): { value: string; style?: React.CSSProperties } | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode] as TextNode;
        if (selectedNode) {
            if (selectedNode.append) {
                return selectedNode.append;
            }
        }
    }
    return null;
});

export const getIconSrc = createSelector(builderState, (state): string | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode?.attr?.src) {
            return selectedNode?.attr?.src as string;
        }
    }
    return null;
});

export const getAllNodeConditions = createSelector(builderState, (state): Condition[] => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode?.conditions) {
            return selectedNode.conditions;
        }
    }
    return [];
});

export const getPaymentDetails = createSelector(builderState, (state): PaymentDetails | null => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode] as PaymentButtonNode;
        if (selectedNode?.paymentDetails) {
            return selectedNode.paymentDetails;
        }
    }
    return null;
});

export const getFilterTargets = createSelector(builderState, (state): Record<string, string> => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer) {
        const filterTargets: Record<string, string> = {};
        let counter = 1;
        Object.values(baseLayer.components).forEach(component => {
            if (
                component.name === "productGrid" ||
                component.name === "productList" ||
                component.name === "orderList" ||
                component.name === "customerList"
            ) {
                filterTargets[component.id] = component?.customName || `${getUIComponent(component.name).label} #${counter}`;
                counter++;
            }
        });
        return filterTargets;
    }
    return {};
});

export const getlinkedInputDisplaysAsTargets = createSelector(builderState, (state): Record<string, string> => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer) {
        const filterTargets: Record<string, string> = {};
        let counter = 1;
        Object.values(baseLayer.components).forEach(component => {
            if (component.name === "linkedInputDisplay") {
                filterTargets[component.id] = component.customName || `${getUIComponent(component.name).label} #${counter}`;
                counter++;
            }
        });
        return filterTargets;
    }
    return {};
});

export const getSlideoutTargets = createSelector(builderState, (state): Record<string, string> => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer) {
        const filterTargets: Record<string, string> = {};
        let counter = 1;
        Object.values(baseLayer.components).forEach(component => {
            if (component.name === "slideout") {
                filterTargets[component.id] = component.customName || `${getUIComponent(component.name).label} #${counter}`;
                counter++;
            }
        });
        return filterTargets;
    }
    return {};
});

export const getDynamicDataType = createSelector(builderState, (state): DynamicDataContext | undefined => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer && state.selectedNode) {
        return getDataContextRecursive(state, state.selectedNode);
    }
});

export const getParentDirection = createSelector(builderState, state => {
    const baseLayer = getBaseLayer(state);
    if (state.selectedNode) {
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode?.parent) {
            const parent = baseLayer.components[selectedNode.parent];
            if (parent.attr?.style?.flexDirection) {
                return parent.attr?.style?.flexDirection;
            }
        }
    }

    return null;
});
